import { useState } from "react";
import { Link } from "react-router-dom";
import { Send, Mail, LogIn } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import SEOHead, { generateWebPageSchema } from "@/components/SEOHead";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";

const SUBJECTS = [
  { value: "", label: "Select a subject" },
  { value: "General Inquiry", label: "General Inquiry" },
  { value: "Listing Help", label: "Listing Help" },
  { value: "Booking Issue", label: "Booking Issue" },
  { value: "Partnership Opportunity", label: "Partnership Opportunity" },
  { value: "Press & Media", label: "Press & Media" },
  { value: "Other", label: "Other" },
];

export default function ContactPageNew() {
  const { user } = useAuth();
  const [form, setForm] = useState({ subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.message.length < 20) {
      toast.error("Please write at least 20 characters.");
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", {
        name: user.name,
        email: user.email,
        subject: form.subject,
        message: `[User ID: ${user.id}]\n[From: ${user.name} <${user.email}>]\n[Subject: ${form.subject}]\n\n${form.message}`,
      });
      setSent(true);
    } catch {
      toast.error("Something went wrong. Please try again or email us at support@paradiseborrowed.com.");
    } finally {
      setSending(false);
    }
  };

  const seoData = {
    title: "Contact Us | Paradise Borrowed",
    description: "Get in touch with the Paradise Borrowed team. General inquiries, listing help, booking issues, partnerships, and press.",
    url: "https://www.paradiseborrowed.com/contact",
  };

  return (
    <>
      <SEOHead title={seoData.title} description={seoData.description} canonicalUrl={seoData.url} schema={generateWebPageSchema(seoData)} />
      <div className="min-h-[60vh] bg-[#FAFAF8]">
        <div className="max-w-2xl mx-auto px-4 py-16" data-testid="contact-page">
          <div className="text-center mb-10">
            <p className="text-xs tracking-widest uppercase text-stone-400 mb-4 font-ui">Contact</p>
            <h1 className="text-4xl sm:text-5xl text-stone-900 mb-3" style={{ fontFamily: "'Fraunces', serif", fontWeight: 400 }}>
              Get in touch
            </h1>
            <p className="text-stone-500 text-sm font-ui">We typically respond within 24–48 hours.</p>
          </div>

          {!user ? (
            /* Logged-out state */
            <div className="bg-white border border-stone-200 rounded-2xl p-10 text-center" data-testid="contact-login-prompt">
              <LogIn className="w-10 h-10 text-stone-300 mx-auto mb-4" />
              <p className="text-stone-700 font-medium mb-2 font-ui">Log in to get in touch with our team.</p>
              <Link to="/auth">
                <Button className="rounded-full px-8 bg-coral hover:bg-[#d44814] text-white mt-3 font-ui" data-testid="contact-login-btn">
                  Log In
                </Button>
              </Link>
              <p className="text-stone-400 text-sm mt-6 font-ui">
                Or email us directly at{" "}
                <a href="mailto:support@paradiseborrowed.com" className="text-coral hover:underline">support@paradiseborrowed.com</a>
              </p>
            </div>
          ) : sent ? (
            /* Success state */
            <div className="bg-white border border-stone-200 rounded-2xl p-10 text-center" data-testid="contact-success">
              <div className="w-14 h-14 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center mx-auto mb-4">
                <Send className="w-6 h-6" />
              </div>
              <h2 className="text-xl text-stone-900 font-medium mb-2" style={{ fontFamily: "'Fraunces', serif" }}>Message sent</h2>
              <p className="text-stone-500 text-sm font-ui">We'll get back to you within 24–48 hours.</p>
            </div>
          ) : (
            /* Logged-in form */
            <div className="bg-white border border-stone-200 rounded-2xl p-8" data-testid="contact-form-card">
              {/* Read-only user info */}
              <div className="grid sm:grid-cols-2 gap-4 mb-6 pb-6 border-b border-stone-100">
                <div>
                  <label className="block text-xs font-medium text-stone-400 mb-1.5 font-ui">Your name</label>
                  <div className="px-4 py-2.5 text-sm bg-stone-50 border border-stone-200 rounded-lg text-stone-700 font-ui" data-testid="contact-readonly-name">{user.name}</div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-stone-400 mb-1.5 font-ui">Your email</label>
                  <div className="px-4 py-2.5 text-sm bg-stone-50 border border-stone-200 rounded-lg text-stone-700 font-ui" data-testid="contact-readonly-email">{user.email}</div>
                </div>
              </div>

              <form onSubmit={handleSubmit} data-testid="contact-form">
                <div className="mb-4">
                  <label className="block text-xs font-medium text-stone-700 mb-1.5 font-ui">Subject</label>
                  <select
                    required
                    value={form.subject}
                    onChange={(e) => setForm({ ...form, subject: e.target.value })}
                    className="w-full px-4 py-2.5 text-sm border border-stone-300 rounded-lg focus:outline-none focus:border-coral transition-colors bg-white font-ui"
                    data-testid="contact-subject"
                  >
                    {SUBJECTS.map((s) => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                </div>

                <div className="mb-6">
                  <label className="block text-xs font-medium text-stone-700 mb-1.5 font-ui">Message</label>
                  <textarea
                    required
                    rows="6"
                    minLength={20}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder="How can we help you?"
                    className="w-full px-4 py-2.5 text-sm border border-stone-300 rounded-lg focus:outline-none focus:border-coral transition-colors resize-none bg-white font-ui"
                    data-testid="contact-message"
                  />
                  <p className="text-xs text-stone-400 mt-1 font-ui">Minimum 20 characters</p>
                </div>

                <Button
                  type="submit"
                  disabled={sending}
                  className="w-full py-3 rounded-lg bg-coral hover:bg-[#d44814] text-white font-medium text-sm font-ui"
                  data-testid="contact-submit"
                >
                  {sending ? "Sending..." : "Send Message"}
                </Button>
              </form>
            </div>
          )}

          <p className="text-center text-xs text-stone-400 mt-8 font-ui">
            You can also reach us at{" "}
            <a href="mailto:support@paradiseborrowed.com" className="text-coral hover:underline">support@paradiseborrowed.com</a>
          </p>
        </div>
      </div>
    </>
  );
}
